import { useEffect } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { ChangeNameFormInterface } from "@interfaces/index";
import { useCommonStore, useUserStore } from "@stores/index";
import { changeNameSchema } from "@utils/index";
import { useUserGetProfile, useUserChangeName } from "@providers/index";
import { useNotify } from "@hooks/index";
import {
  Form,
  FormRow,
  FormInput,
  Button,
  MobileHeader,
} from "@components/index";

export const ChangeNameForm = () => {
  const setCommonState = useCommonStore(({ setState }) => setState);
  const setUserState = useUserStore(({ setState }) => setState);
  const notify = useNotify();

  const {
    isPending: isPendingGetProfile,
    isSuccess,
    data,
    isError,
    error,
  } = useUserGetProfile();

  const { mutate: mutateUserChangeName, isPending: isPendingChangeName } =
    useUserChangeName(
      ({ firstName, lastName }) => {
        setUserState({ userFullName: `${firstName} ${lastName}` });
        notify("success", "Your name has been successfully updated.");
      },
      ({ message, fields }) => {
        Object.keys(fields || {}).forEach((key) =>
          setError(key as "firstName" | "lastName", { message: fields![key] }),
        );
        notify("error", message);
      },
    );

  useEffect(() => {
    setCommonState({ isLoading: isPendingGetProfile || isPendingChangeName });
  }, [isPendingGetProfile, isPendingChangeName, setCommonState]);

  const {
    register,
    handleSubmit,
    formState,
    reset,
    clearErrors,
    setError,
    setValue,
  } = useForm<ChangeNameFormInterface>({
    mode: "onBlur",
    disabled: isPendingGetProfile || isPendingChangeName,
    resolver: yupResolver(changeNameSchema),
  });

  useEffect(() => {
    if (isSuccess && data) {
      reset({ ...data });
    }
  }, [isSuccess, data, reset]);

  useEffect(() => {
    if (isError && error) {
      notify("error", error.message);
    }
  }, [isError, error, notify]);

  const onSubmit: SubmitHandler<ChangeNameFormInterface> = (data) =>
    mutateUserChangeName({ ...data });

  return (
    <>
      <MobileHeader href="/profile" title="Change Name" />
      <Form
        onSubmit={handleSubmit(onSubmit)}
        className="px-4 text-black-100 md:max-w-[632px] md:px-24"
      >
        <FormRow top="large">
          <FormInput
            {...register<"firstName">("firstName")}
            label="First name"
            placeholder="First name *"
            error={formState.errors.firstName?.message}
            onBlur={({ currentTarget }) => {
              setValue("firstName", currentTarget.value.trim());
            }}
            onChange={() => clearErrors("firstName")}
          />
        </FormRow>
        <FormRow>
          <FormInput
            {...register<"lastName">("lastName")}
            label="Last name"
            placeholder="Last name *"
            error={formState.errors.lastName?.message}
            onBlur={({ currentTarget }) => {
              setValue("lastName", currentTarget.value.trim());
            }}
            onChange={() => clearErrors("lastName")}
          />
        </FormRow>
        <FormRow isRow isBetween top="large">
          <Button type="submit" title="Save" className="w-52" />
          <Button
            title="Cancel"
            variant="text"
            isLink
            href="/profile"
            className="text-black-54"
          />
        </FormRow>
      </Form>
    </>
  );
};
